import type { ContractData } from "../contexts/ContractContext";
import type { ContractCompany, ContractType } from "../services/contract-types";

const UPDATE_RELOAD_MARKER_KEY = "d2d:update_reload_marker";
const CONTRACT_CARD_DRAFT_KEY = "d2d:draft:contract_card";
const NEW_CONTRACT_MODAL_DRAFT_KEY = "d2d:draft:new_contract_modal";
const NEW_CONTRACT_MODAL_OPEN_STATE_KEY = "d2d:draft:new_contract_modal_state";

interface UpdateReloadMarker {
  reason: "version_update";
  timestamp: number;
}

interface NewContractModalOpenState {
  customerId: number;
  wasOpen: boolean;
  timestamp: number;
}

export interface UtilityContractDetailsDraft {
  previous_provider_name: string;
  previous_customer_number: string;
  annual_consumption_kwh: string;
  meter_point_id: string;
  meter_number: string;
  meter_reading: string;
  unit_price_ct_kwh: string;
  base_price_eur_month: string;
  delivery_start_date: string;
  as_soon_as_possible: boolean;
  desired_monthly_payment: string;
}

export interface ContractCardDraftSnapshot {
  timestamp: number;
  contractData: ContractData;
  birthDateText: string;
  useDateTextInput: boolean;
  electricityDetails: UtilityContractDetailsDraft;
  gasDetails: UtilityContractDetailsDraft;
  searchQuery: string;
  currentPage: number;
  customerSignatureDataUrl: string | null;
}

export interface NewContractModalDraftSnapshot {
  timestamp: number;
  customerId: number;
  wasOpen: boolean;
  selectedCategory: string;
  selectedContractTypes: (ContractType & { company_details: ContractCompany })[];
  signature: string | null;
  bankDetails: string;
  birthDateText: string;
  useDateTextInput: boolean;
  gender: number | null;
  electricityDetails: UtilityContractDetailsDraft;
  gasDetails: UtilityContractDetailsDraft;
  searchQuery: string;
  currentPage: number;
  customerSignatureDataUrl: string | null;
}

let updateReloadSessionActive = false;

const canUseSessionStorage = (): boolean =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isUtilityDetailsDraft = (
  value: unknown
): value is UtilityContractDetailsDraft => {
  if (!isRecord(value)) return false;

  return (
    typeof value["previous_provider_name"] === "string" &&
    typeof value["previous_customer_number"] === "string" &&
    typeof value["annual_consumption_kwh"] === "string" &&
    typeof value["meter_point_id"] === "string" &&
    typeof value["meter_number"] === "string" &&
    typeof value["meter_reading"] === "string" &&
    typeof value["unit_price_ct_kwh"] === "string" &&
    typeof value["base_price_eur_month"] === "string" &&
    typeof value["delivery_start_date"] === "string" &&
    typeof value["as_soon_as_possible"] === "boolean" &&
    typeof value["desired_monthly_payment"] === "string"
  );
};

const isContractCardDraftSnapshot = (
  value: unknown
): value is ContractCardDraftSnapshot => {
  if (!isRecord(value)) return false;

  return (
    typeof value["timestamp"] === "number" &&
    isRecord(value["contractData"]) &&
    typeof value["birthDateText"] === "string" &&
    typeof value["useDateTextInput"] === "boolean" &&
    isUtilityDetailsDraft(value["electricityDetails"]) &&
    isUtilityDetailsDraft(value["gasDetails"]) &&
    typeof value["searchQuery"] === "string" &&
    typeof value["currentPage"] === "number" &&
    (typeof value["customerSignatureDataUrl"] === "string" ||
      value["customerSignatureDataUrl"] === null)
  );
};

const isNewContractModalDraftSnapshot = (
  value: unknown
): value is NewContractModalDraftSnapshot => {
  if (!isRecord(value)) return false;

  return (
    typeof value["timestamp"] === "number" &&
    typeof value["customerId"] === "number" &&
    typeof value["wasOpen"] === "boolean" &&
    typeof value["selectedCategory"] === "string" &&
    Array.isArray(value["selectedContractTypes"]) &&
    (typeof value["signature"] === "string" || value["signature"] === null) &&
    typeof value["bankDetails"] === "string" &&
    typeof value["birthDateText"] === "string" &&
    typeof value["useDateTextInput"] === "boolean" &&
    (typeof value["gender"] === "number" || value["gender"] === null) &&
    isUtilityDetailsDraft(value["electricityDetails"]) &&
    isUtilityDetailsDraft(value["gasDetails"]) &&
    typeof value["searchQuery"] === "string" &&
    typeof value["currentPage"] === "number" &&
    (typeof value["customerSignatureDataUrl"] === "string" ||
      value["customerSignatureDataUrl"] === null)
  );
};

const isUpdateReloadMarker = (value: unknown): value is UpdateReloadMarker => {
  if (!isRecord(value)) return false;

  return (
    value["reason"] === "version_update" &&
    typeof value["timestamp"] === "number"
  );
};

const isNewContractModalOpenState = (
  value: unknown
): value is NewContractModalOpenState => {
  if (!isRecord(value)) return false;

  return (
    typeof value["customerId"] === "number" &&
    typeof value["wasOpen"] === "boolean" &&
    typeof value["timestamp"] === "number"
  );
};

const writeSessionValue = (key: string, value: unknown): void => {
  if (!canUseSessionStorage()) return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to write session storage key: ${key}`, error);
  }
};

const readSessionValue = <T>(
  key: string,
  validator: (value: unknown) => value is T
): T | null => {
  if (!canUseSessionStorage()) return null;

  const raw = window.sessionStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (validator(parsed)) {
      return parsed;
    }

    window.sessionStorage.removeItem(key);
    return null;
  } catch (error) {
    console.error(`Failed to parse session storage key: ${key}`, error);
    window.sessionStorage.removeItem(key);
    return null;
  }
};

const removeSessionValue = (key: string): void => {
  if (!canUseSessionStorage()) return;
  window.sessionStorage.removeItem(key);
};

export const markVersionReloadPending = (): void => {
  writeSessionValue(UPDATE_RELOAD_MARKER_KEY, {
    reason: "version_update",
    timestamp: Date.now(),
  } satisfies UpdateReloadMarker);
};

export const initUpdateReloadSession = (): void => {
  const marker = readSessionValue(UPDATE_RELOAD_MARKER_KEY, isUpdateReloadMarker);
  updateReloadSessionActive = !!marker;
  removeSessionValue(UPDATE_RELOAD_MARKER_KEY);
};

export const isUpdateReloadSessionActive = (): boolean => updateReloadSessionActive;

export const saveContractCardDraft = (
  snapshot: ContractCardDraftSnapshot
): void => {
  writeSessionValue(CONTRACT_CARD_DRAFT_KEY, snapshot);
};

export const getContractCardDraft = (): ContractCardDraftSnapshot | null =>
  readSessionValue(CONTRACT_CARD_DRAFT_KEY, isContractCardDraftSnapshot);

export const clearContractCardDraft = (): void => {
  removeSessionValue(CONTRACT_CARD_DRAFT_KEY);
};

export const saveNewContractModalDraft = (
  snapshot: NewContractModalDraftSnapshot
): void => {
  writeSessionValue(NEW_CONTRACT_MODAL_DRAFT_KEY, snapshot);
};

export const getNewContractModalDraft = (): NewContractModalDraftSnapshot | null =>
  readSessionValue(NEW_CONTRACT_MODAL_DRAFT_KEY, isNewContractModalDraftSnapshot);

export const clearNewContractModalDraft = (): void => {
  removeSessionValue(NEW_CONTRACT_MODAL_DRAFT_KEY);
};

export const saveNewContractModalOpenState = (customerId: number): void => {
  writeSessionValue(NEW_CONTRACT_MODAL_OPEN_STATE_KEY, {
    customerId,
    wasOpen: true,
    timestamp: Date.now(),
  } satisfies NewContractModalOpenState);
};

export const getNewContractModalOpenState =
  (): NewContractModalOpenState | null =>
    readSessionValue(
      NEW_CONTRACT_MODAL_OPEN_STATE_KEY,
      isNewContractModalOpenState
    );

export const clearNewContractModalOpenState = (): void => {
  removeSessionValue(NEW_CONTRACT_MODAL_OPEN_STATE_KEY);
};
